import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import SectionTabs from 'components/Layout/MobileShell/SectionTabs';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import MobileFab from 'components/Layout/MobileShell/MobileFab';
import MobileListState from 'components/Layout/MobileShell/MobileListState';
import MobileToolbar from 'components/Layout/MobileShell/MobileToolbar';
import { onClick as onNewUserClick } from 'components/Admin/Users/NewUserButton';
import { onTextChange } from 'helper/controlledField';
import { ListedUser } from 'helper/users';
import { ListedProduct } from 'helper/products';
import { ListedCompany } from 'helper/companies';
import { UserModalFilters, DEFAULT_USER_FILTERS } from 'helper/userFilters';
import { closeModal, openModal } from 'helper/modals';
import useConfirmationDialogBox from 'utils/useConfirmationDialogBox';
import ADMIN_SECTION_TABS from 'constants/adminSectionTabs';

import UserCard from './UserCard';
import FilterSheet from './FilterSheet';
import FormSheet from './FormSheet';

import './index.scss';

export default function UsersMobile({
  data,
  isLoading,
  isFetching,
  listEnded,
  searchBarText,
  setSearchBarText,
  setSearchQuery,
  filters,
  setFilters,
  filterActive,
  appliedFilters,
  productsData,
  productsLoading,
  companiesData,
  companiesLoading,
}: {
  data: ListedUser[] | undefined,
  isLoading: boolean,
  isFetching: boolean,
  listEnded: boolean,
  searchBarText: string,
  setSearchBarText: React.Dispatch<React.SetStateAction<string>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  filters: UserModalFilters,
  setFilters: React.Dispatch<React.SetStateAction<UserModalFilters>>,
  filterActive: boolean,
  appliedFilters: React.ReactNode[],
  productsData: ListedProduct[] | undefined,
  productsLoading: boolean,
  companiesData: ListedCompany[] | undefined,
  companiesLoading: boolean,
}) {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const { confirmationDialogBox, setConfirmationDialogBox } = useConfirmationDialogBox();

  const clearAllFilters = () => {
    setFilters(DEFAULT_USER_FILTERS);
    setSearchQuery('');
    setSearchBarText('');
  };

  const resultsCount = data?.length || 0;

  return (
    <div className="users-mobile">
      <MobileSectionHeader
        backLabel="Administração"
        title="Usuários"
        onBack={() => navigate('/admin')}
        onAvatarClick={() => setMoreOpen(true)}
      />
      <SectionTabs tabs={ADMIN_SECTION_TABS} sheetTitle="Administração" />

      <div className="users-mobile-content">
        <MobileToolbar
          searchValue={searchBarText}
          onSearchChange={onTextChange(setSearchBarText)}
          setSearchQuery={setSearchQuery}
          searchId="users-mobile-search-bar"
          searchPlaceholder="Pesquisar usuário"
          filterActive={filterActive}
          filterAriaLabel="Filtrar usuários"
          appliedFilters={appliedFilters}
          onFilterClick={openModal(setFilterSheetOpen)}
          onClearAll={clearAllFilters}
          resultsCount={resultsCount}
          resultsNoun="usuário"
          loading={isLoading}
        />

        <MobileListState
          loading={isLoading}
          isEmpty={resultsCount === 0}
          emptyMessage="Nenhum usuário encontrado."
          infiniteScroll={{ fetchingMore: isFetching, listEnded }}
        >
          {data?.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              setConfirmationDialogBox={setConfirmationDialogBox}
            />
          ))}
        </MobileListState>
      </div>

      <MobileFab label="Novo usuário" onClick={onNewUserClick} />

      <FilterSheet
        open={filterSheetOpen}
        closeModal={closeModal(setFilterSheetOpen)}
        filters={filters}
        setFilters={setFilters}
        productsLoading={productsLoading}
        products={productsData}
        companiesLoading={companiesLoading}
        companies={companiesData}
      />
      <FormSheet />

      {confirmationDialogBox}
      <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
    </div>
  );
}
